import { useEffect, useState } from "react";
import { RotateCcw, Save, ShieldCheck, Loader2, Pencil, Check } from "lucide-react";
import { useAuth } from "../../store/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { useWorkspace } from "../../store/WorkspaceContext";
import { pathD, bbox } from "../../engine/geometry";

const Thumb = ({ points }) => {
  const bb = bbox(points);
  return <svg viewBox={`${bb.minX - 1} ${bb.minY - 1} ${bb.w + 2} ${bb.h + 2}`} className="h-10 w-16 flex-shrink-0"><path d={pathD(points)} fill="#4c9ffe33" stroke="#4c9ffe" strokeWidth={bb.w / 80} /></svg>;
};

export const VersionHistoryDialog = () => {
  const { state, dispatch } = useWorkspace();
  const open = state.dialog === "versions";
  const sel = state.pieces.find((p) => p.id === state.selectedId);
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const [editName, setEditName] = useState(null);
  const canRename = !!sel && user?.role === "admin";
  const close = () => dispatch({ type: "SET", patch: { dialog: null } });
  const rename = async () => {
    const name = (editName || "").trim();
    if (!name || name === sel.name) return setEditName(null);
    setBusy(true);
    try { await api.renamePattern(sel.patternId, name); dispatch({ type: "UPDATE_PIECE", id: sel.id, patch: { name } }); toast.success(`Renamed to “${name}”`); setEditName(null); }
    catch { toast.error("Rename failed"); } finally { setBusy(false); }
  };

  const refresh = () => sel && api.versions(sel.patternId).then(setData).catch(() => toast.error("Failed to load versions"));
  useEffect(() => { if (open) { setData(null); refresh(); } }, [open, sel?.patternId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyVersion = (res, msg) => {
    dispatch({ type: "UPDATE_PIECE", id: sel.id, patch: { points: res.points, basePoints: res.points, version: res.version, dirty: false, appliedOffset: 0 }, commit: true });
    toast.success(msg);
    refresh();
  };
  const save = async () => {
    setBusy(true);
    try { applyVersion(await api.saveVersion(sel.patternId, { points: sel.points, notes: notes || "Geometry edit from workspace" }), `${sel.name} saved as v${data.versions[0].version + 1}`); setNotes(""); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const rollback = async (v) => {
    setBusy(true);
    try { applyVersion(await api.rollback(sel.patternId, v.version), `${sel.name} rolled back to v${v.version} geometry`); }
    catch { toast.error("Rollback failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="panel3d max-w-xl border-[#27324a] bg-[#121824] text-[#e8e9ec]" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <span className="flex-shrink-0">PATTERN VERSIONS ·</span>
            {editName === null ? (
              <>
                <span className="truncate" data-testid="version-pattern-name">{sel?.name || "—"}</span>
                {canRename && <button data-testid="rename-pattern-btn" title="Rename this template" onClick={() => setEditName(sel.name)} className="rounded p-1 text-[#9aa0a8] hover:bg-white/10 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>}
              </>
            ) : (
              <>
                <input data-testid="rename-pattern-input" autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setEditName(null); }} className="field h-7 flex-1 py-0 text-[13px] font-normal normal-case" maxLength={80} />
                <button data-testid="rename-pattern-save" disabled={busy} onClick={rename} className="btn h-7 px-2 text-[11px]"><Check className="h-3.5 w-3.5" /> Save</button>
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-[#9aa0a8]">Every edit and rollback is a new immutable version. Production-safe versions are never silently replaced.</DialogDescription>
        </DialogHeader>
        {!sel ? <div className="text-xs text-[#6b7079]">Select a piece first.</div> : !data ? <div className="flex items-center gap-2 text-xs text-[#9aa0a8]"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div> : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[11px] text-[#3ecf8e]" data-testid="version-publisher"><ShieldCheck className="h-3.5 w-3.5" />{data.pattern.publisher_name} · Tier {data.pattern.publisher_tier} · {data.pattern.status}</div>
            {sel.dirty && (
              <div className="flex gap-2 rounded-sm border border-[#f5a524]/40 bg-[#f5a524]/10 p-2">
                <input data-testid="version-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Change notes (e.g. shrunk top edge 1/16 for 2022 trim)" className="field flex-1" />
                <button data-testid="save-version-btn" disabled={busy} onClick={save} className="btn-red"><Save className="h-3.5 w-3.5" /> Save v{data.versions[0].version + 1}</button>
              </div>
            )}
            <ul className="max-h-72 space-y-1.5 overflow-y-auto" data-testid="version-list">
              {data.versions.map((v) => (
                <li key={v.version} data-testid={`version-row-${v.version}`} className={`flex items-center gap-3 rounded-sm border p-2 ${v.is_current ? "border-[#4c9ffe]/60 bg-[#4c9ffe]/10" : "border-[#27324a] bg-[#1a2231]"}`}>
                  <Thumb points={v.points} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs"><span className="font-mono font-semibold">v{v.version}</span>{v.is_current && <span className="rounded-sm bg-[#4c9ffe]/20 px-1 font-mono text-[9px] text-[#4c9ffe]">CURRENT</span>}<span className="rounded-sm bg-[#0c1017] px-1 font-mono text-[9px] uppercase text-[#9aa0a8]">{v.source}</span></div>
                    <div className="truncate text-[11px] text-[#9aa0a8]">{v.notes}</div>
                    <div className="font-mono text-[10px] text-[#6b7079]">{v.author} · {new Date(v.created_at).toLocaleString()} · {v.point_count} nodes · {(v.area_sqin / 144).toFixed(2)} sq ft</div>
                  </div>
                  {!v.is_current && <button data-testid={`rollback-btn-${v.version}`} disabled={busy} onClick={() => rollback(v)} className="btn"><RotateCcw className="h-3.5 w-3.5" /> Rollback</button>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
